import React, { FC, useState, useRef, useEffect } from "react";
import classNames from "classnames";
import { Player, BigPlayButton, PlayerReference } from "video-react";
import { isIOS } from "react-device-detect";
import {
  canSeeHiddenNames,
  nameIfHidden,
  renderHatIfUserNameIsHidden,
} from "../../../../utils/user.utils";
import { Idea } from "../../../../types/Idea.interface";
import { User } from "../../../../types/User.interface";
import styles from "./IdeaPhotoCard.module.scss";
import EventsTrackWrapperClick from "../../../containers/EventsTrack/wrappers/EventsTrackWrapperClick";
import Text from "../../Typography/Text";
import Share from "../../Share/Share";
import Like from "../../Controls/Like/Like";
import { useIsMobile, useIsTablet } from "../../../../hooks/useMedia";

import Platform from "../../../../services/platform";

const isMobileDevice = Platform.isMobileDevice();

interface Props {
  isMediaCard?: boolean;
  idea?: Idea | any;
  user?: User;
  onCardClick?: (idea: Idea) => void;
  onAuthorClick?: (idea: Idea) => void;
  onTagClick?: (idea: Idea) => void | null;
  onLike?: (idea: Idea) => void;
  className?: string;
  isModalView?: boolean;
  isGridView?: boolean;
}

const IdeaPhotoCard: FC<Props> = ({
  user,
  idea,
  isMediaCard,
  onLike,
  onAuthorClick,
  onTagClick,
  onCardClick,
  className,
  isModalView = false,
  isGridView = false,
}) => {
  const [pointed, setPointed] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const playerRef = useRef<PlayerReference | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const isTablet = useIsTablet();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (playerRef.current) {
      if (isPlaying) {
        playerRef.current.play();
      } else {
        playerRef.current.pause();
      }
    }
  }, [isPlaying]);

  useEffect(
    () => () => {
      if (isModalView) {
        setPlaying(false);
      }
    },
    [isModalView]
  );

  useEffect(() => {
    if (!isMediaCard || isModalView || !idea?.videoAttachment) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        setPlaying(visible);
      },
      {
        threshold: [0, 0.5, 1],
      }
    );

    const { current } = cardRef;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [isMediaCard, isModalView, idea?.videoAttachment]);

  const getName = () => {
    const author = idea?.author;
    if (!author) return isMediaCard ? "Город идей" : "";
    if (author.deletedSudir || (!author.showName && !canSeeHiddenNames(user))) {
      return nameIfHidden;
    }

    return `${author.lastName} ${idea.author.firstName && idea.author.firstName[0]}.`;
  };

  const getTitle = () =>
    `Идея «${idea?.ideaName}» предложена на платформе «Город идей» и реализована городом.`;

  const renderInfo = () => {
    const authorName = getName();
    const hidden = !pointed && !isMobileDevice && !isModalView;

    return (
      <div
        className={classNames(styles.info, {
          [styles.info_hidden]: hidden,
        })}
      >
        <div className={styles.header}>
          <EventsTrackWrapperClick needParent id={175} replace={[authorName]}>
            <div
              className={styles.author}
              onClick={(e) => {
                e.stopPropagation();
                onAuthorClick?.(idea);
              }}
            >
              <Text size={4} ellipsis className={styles.author__name}>
                {authorName}
              </Text>
              {renderHatIfUserNameIsHidden(idea.author, user)}
            </div>
          </EventsTrackWrapperClick>
          {isModalView && idea?.number && (
            <Text size={4} className={styles.number}>
              #{idea.number}
            </Text>
          )}
        </div>

        {!isModalView ? (
          <Text
            className={styles["idea-name"]}
            size={3}
            ellipsis={3}
            weight={600}
          >
            {idea?.ideaName}
          </Text>
        ) : (
          <div className={styles["idea-details"]}>
            {idea?.ideaName && (
              <Text size={isTablet && isModalView ? 4 : 3}>
                <b>Идея:</b> {idea.ideaName}
              </Text>
            )}
            {idea?.address && (
              <Text size={isTablet && isModalView ? 4 : 3}>
                <b>Адрес:</b> {idea.address}
              </Text>
            )}
            {idea?.comment && idea?.showComment && (
              <Text size={isTablet && isModalView ? 4 : 3}>
                <b>Комментарий:</b> {idea.comment}
              </Text>
            )}
          </div>
        )}
        <div className={styles.bottom}>
          {!isMediaCard && (
            <div
              className={styles.tag}
              onClick={(e) => {
                e.stopPropagation();
                onTagClick?.(idea);
              }}
            >
              <Text
                size={4}
                ellipsis
                uppercase
                weight={500}
                className={styles.tag__text}
              >
                {idea?.tag?.name}
              </Text>
            </div>
          )}
          <div className={styles.share}>
            <Share
              url={`/ideas/${isMediaCard ? idea.mediaIdeaId : idea?.id}${idea?.isMediaCard ? "?isMedia=true" : ""}`}
              imageUrl={`/uploads/get/${idea?.watermarkAttachmentId}`}
              title={getTitle()}
              backgroundColorIcon="#FFFFFF29"
              backgroundColorPanelIcon="white"
              colorIcon="white"
              colorPanelIcon="black"
              canOpen={!hidden}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={classNames(styles["idea-photo-card"], className, {
        [styles.modalView]: isModalView,
        [styles.mediaCard]: isMediaCard,
        [styles.gridView]: isGridView,
      })}
      onClick={() => onCardClick?.(idea)}
      onPointerEnter={() => setPointed(true)}
      onPointerLeave={() => setPointed(false)}
      ref={cardRef}
    >
      {isMediaCard && Boolean(idea.videoAttachment) ? (
        <>
          {idea?.watermarkAttachmentId && (
            <img
              className={classNames(styles.poster, {
                [styles.hidden]: isPlaying || (isModalView && !isIOS),
              })}
              src={`/uploads/get/${idea.watermarkAttachmentId}`}
              alt={idea.ideaName}
            />
          )}
          <Player
            key={idea?.videoAttachment || ""}
            ref={playerRef}
            playsInline={!(isIOS && isMobile && isModalView)}
            fluid={false}
            muted
            width={"100%" as any}
            height={"100%" as any}
            className={classNames(styles.player, {
              [styles["no-controls"]]: !isModalView,
              [styles.hidden]: !isPlaying && !isModalView,
            })}
          >
            <source
              src={`/uploads/get/${idea?.videoAttachment}`}
              type="video/mp4"
            />
            <BigPlayButton className={styles["big-play-button"]} />
          </Player>
          {!isPlaying && !isModalView && (
            <div className={styles["play-container"]}>
              <button
                type="button"
                className={styles["play-icon"]}
                onClick={() => setPlaying(true)}
              />
            </div>
          )}
          {isModalView && !isPlaying && (
            <button
              className={classNames(styles["play-button"], {
                [styles.hidden]: !isModalView,
              })}
              type="button"
              onClick={() => setPlaying(true)}
            />
          )}
        </>
      ) : (
        idea?.watermarkAttachmentId && (
          <img
            className={styles.bg}
            src={`/uploads/get/${idea.watermarkAttachmentId}`}
            alt={idea.ideaName}
          />
        )
      )}

      <EventsTrackWrapperClick needParent id={176}>
        <div className={classNames(styles.like)}>
          <Like
            onClick={(e) => {
              e.stopPropagation();
              onLike?.(idea);
            }}
            count={idea?.rating}
            liked={idea?.liked && user?.loggedIn}
          />
        </div>
      </EventsTrackWrapperClick>

      {renderInfo()}
    </div>
  );
};

export default IdeaPhotoCard;
